import { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Download, Loader2, FileText, Mail } from "lucide-react";
import CopyLinkButton from "@/components/CopyLinkButton";

interface ResourcePreviewModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  fileUrl: string;
  resourceType: string;
  accessType: string;
  onDownload: () => void;
  /** Public, shareable link to this resource (enables Copy link / Email). */
  shareUrl?: string;
}

/**
 * One code path for all resources: the file URL is embedded directly in an
 * iframe. Renderer-backed (html-kind) assets are served as an inline PDF by
 * `public-asset-file`, so they behave exactly like a stored PDF here.
 */
export default function ResourcePreviewModal({
  open, onOpenChange, title, fileUrl, resourceType, accessType, onDownload, shareUrl,
}: ResourcePreviewModalProps) {
  const [iframeLoading, setIframeLoading] = useState(true);
  const [iframeFailed, setIframeFailed] = useState(false);

  // Browsers render PDFs natively via <iframe>. Appending #view=FitH gives a
  // sensible default zoom in Chrome/Edge/Firefox.
  const previewUrl = fileUrl
    ? resourceType === "pdf"
      ? `${fileUrl}#view=FitH&toolbar=1`
      : fileUrl
    : "";

  const handleOpenChange = (val: boolean) => {
    if (!val) {
      setIframeLoading(true);
      setIframeFailed(false);
    }
    onOpenChange(val);
  };

  // A cold server-side PDF render measures ~6.2s, so a 5s cut-off produced a
  // false "preview not available". The spinner is held until `onLoad` fires and
  // the safety net is 30s — long enough for a cold render, short enough to
  // still rescue a browser that genuinely blocks embedded PDFs.
  useEffect(() => {
    if (!open) return;
    setIframeLoading(true);
    setIframeFailed(false);
    const t = setTimeout(() => {
      setIframeLoading((stillLoading) => {
        if (stillLoading) setIframeFailed(true);
        return stillLoading;
      });
    }, 30000);
    return () => clearTimeout(t);
  }, [open, fileUrl]);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-4xl w-[95vw] h-[90vh] flex flex-col p-0 gap-0 [&>button:last-child]:hidden">
        {/* Header */}
        <DialogHeader className="px-6 py-4 border-b border-border flex-shrink-0">
          <div className="flex items-center justify-between gap-4">
            <DialogTitle className="text-lg font-semibold text-foreground truncate pr-4">
              {title}
            </DialogTitle>
            <div className="flex items-center gap-2 flex-shrink-0">
              {shareUrl && (
                <>
                  <CopyLinkButton url={shareUrl} title={`Copy link to ${title}`} />
                  <Button asChild variant="outline" size="sm" className="gap-2">
                    <a
                      href={`mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(
                        `Thought this might be useful:\n\n${title}\n${shareUrl}`,
                      )}`}
                    >
                      <Mail className="w-4 h-4" />
                      Email
                    </a>
                  </Button>
                </>
              )}
              <Button onClick={onDownload} size="sm" className="gap-2">
                <Download className="w-4 h-4" />
                Download
              </Button>
              <DialogClose asChild>
                <Button variant="outline" size="sm">
                  Close
                </Button>
              </DialogClose>
            </div>
          </div>
        </DialogHeader>

        {/* Preview area */}
        <div className="flex-1 relative min-h-0 bg-muted">
          {iframeLoading && !iframeFailed && (
            <div className="absolute inset-0 flex items-center justify-center z-10 bg-muted">
              <div className="text-center space-y-3">
                <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto" />
                <p className="text-sm text-muted-foreground">Loading preview…</p>
              </div>
            </div>
          )}
          {iframeFailed && previewUrl ? (
            <div className="absolute inset-0 z-20 flex items-center justify-center bg-muted">
              <div className="max-w-md text-center space-y-4 p-6">
                <div className="w-16 h-16 mx-auto rounded-xl bg-primary/10 flex items-center justify-center">
                  <FileText className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-lg font-semibold text-foreground">
                  Preview not available in this browser
                </h3>
                <p className="text-sm text-muted-foreground">
                  Some browsers block embedded PDF previews. Download the file to read it instead.
                </p>
                <div className="flex items-center justify-center gap-2">
                  <Button onClick={onDownload} className="gap-2">
                    <Download className="w-4 h-4" />
                    Download
                  </Button>
                </div>
              </div>
            </div>
          ) : null}
          {previewUrl ? (
            <iframe
              src={previewUrl}
              title={title}
              className="w-full h-full border-0"
              onLoad={() => {
                setIframeLoading(false);
                setIframeFailed(false);
              }}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <FileText className="w-16 h-16 text-primary/30" />
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
